import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { PageHeader } from "@/components/ui/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";
import { Plus, Edit, Trash2, Settings, CheckCircle, XCircle, Clock, FileText } from "lucide-react";
import { useLocation } from "wouter";
import { getJson } from "@/lib/http";
import { apiRequest } from "@/lib/queryClient";
import CampaignTypes from "./admin/GeneralSettings/CampaignTypes";
import MobileNotifications from "./admin/GeneralSettings/MobileNotifications";

type CampaignStatus = {
  id: number;
  name: string;
  description?: string;
  color: string;
  sortOrder: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
};

const defaultStatuses = [
  { name: "Draft", description: "Campaign is being created and not yet confirmed", color: "#6B7280", sortOrder: 1 },
  { name: "Confirmed", description: "Campaign has been confirmed and is ready for artwork", color: "#3B82F6", sortOrder: 2 },
  { name: "Artwork Received", description: "Artwork has been received and is pending approval", color: "#F59E0B", sortOrder: 3 },
  { name: "Artwork Approved", description: "Artwork has been approved and is ready for printing", color: "#10B981", sortOrder: 4 },
  { name: "Printed", description: "Campaign materials have been printed", color: "#8B5CF6", sortOrder: 5 },
  { name: "Posted", description: "Campaign has been posted in stores", color: "#06B6D4", sortOrder: 6 },
  { name: "Posting Confirmed", description: "Posting has been confirmed by operators", color: "#22C55E", sortOrder: 7 },
  { name: "Removed", description: "Campaign materials have been removed", color: "#EF4444", sortOrder: 8 },
];

const defaultTypes = [
  { name: "Standard", description: "Regular campaigns with standard timelines", sortOrder: 1 },
  { name: "Promotional", description: "Promotional campaigns with special pricing", sortOrder: 2 },
  { name: "Seasonal", description: "Seasonal campaigns for holidays and special events", sortOrder: 3 },
  { name: "Event-Based", description: "Event-specific campaigns with targeted messaging", sortOrder: 4 },
  { name: "Limited Time", description: "Limited time offers with urgency", sortOrder: 5 },
  { name: "Custom", description: "Custom campaign types for specific needs", sortOrder: 6 },
];

export default function CampaignStatusManagement() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();

  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [editingStatus, setEditingStatus] = useState<CampaignStatus | null>(null);

  // Form states
  const [statusForm, setStatusForm] = useState({
    name: "",
    description: "",
    color: "#6B7280",
    sortOrder: 0,
    isActive: true,
  });



  // Data queries
  const { data: statuses = [], isLoading: statusesLoading } = useQuery<CampaignStatus[]>({
    queryKey: ["/api/admin/campaign-statuses"],
    queryFn: () => getJson<CampaignStatus[]>("/api/admin/campaign-statuses"),
  });

  // Mutations
  const createStatusMutation = useMutation({
    mutationFn: async (data: typeof statusForm) => {
      const response = await apiRequest("/api/admin/campaign-statuses", "POST", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/campaign-statuses"] });
      setIsStatusDialogOpen(false);
      resetStatusForm();
      toast({ title: "Success", description: "Campaign status created successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to create campaign status", variant: "destructive" });
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, data }: { id: number; data: any }) => {
      const response = await apiRequest(`/api/admin/campaign-statuses/${id}`, "PUT", data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/campaign-statuses"] });
      setIsStatusDialogOpen(false);
      setEditingStatus(null);
      resetStatusForm();
      toast({ title: "Success", description: "Campaign status updated successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to update campaign status", variant: "destructive" });
    },
  });

  const deleteStatusMutation = useMutation({
    mutationFn: async (id: number) => {
      const response = await apiRequest(`/api/admin/campaign-statuses/${id}`, "DELETE");
      return response.json().catch(() => null);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/campaign-statuses"] });
      toast({ title: "Success", description: "Campaign status deleted successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to delete campaign status", variant: "destructive" });
    },
  });




  const createDefaultStatusesMutation = useMutation({
    mutationFn: async () => {
      for (const status of defaultStatuses) {
        try {
          await apiRequest("/api/admin/campaign-statuses", "POST", status);
        } catch (error: any) {
          if (error?.status !== 409) {
            throw error;
          }
          // Ignore duplicate errors when seeding defaults
        }
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/campaign-statuses"] });
      toast({ title: "Success", description: "Default campaign statuses created successfully" });
    },
    onError: (error: Error) => {
      toast({ title: "Error", description: error.message || "Failed to create default statuses", variant: "destructive" });
    },
  });

  // Form helpers
  const resetStatusForm = () => {
    setStatusForm({
      name: "",
      description: "",
      color: "#6B7280",
      sortOrder: 0,
      isActive: true,
    });
  };

  // Event handlers
  const handleCreateStatus = () => {
    createStatusMutation.mutate(statusForm);
  };

  const handleUpdateStatus = () => {
    if (editingStatus) {
      updateStatusMutation.mutate({ id: editingStatus.id, data: statusForm });
    }
  };

  const handleEditStatus = (status: CampaignStatus) => {
    setEditingStatus(status);
    setStatusForm({
      name: status.name,
      description: status.description || "",
      color: status.color,
      sortOrder: status.sortOrder,
      isActive: status.isActive,
    });
    setIsStatusDialogOpen(true);
  };

  const getStatusIcon = (status: CampaignStatus) => {
    const name = status.name.toLowerCase();
    if (name.includes("draft")) return <FileText className="h-4 w-4" />;
    if (name.includes("confirmed") || name.includes("approved")) return <CheckCircle className="h-4 w-4" />;
    if (name.includes("pending") || name.includes("received")) return <Clock className="h-4 w-4" />;
    if (name.includes("removed") || name.includes("cancelled")) return <XCircle className="h-4 w-4" />;
    return <Settings className="h-4 w-4" />;
  };

  const orderedStatuses = [...statuses].sort((a, b) => {
    if (a.sortOrder === b.sortOrder) {
      return a.id - b.id;
    }
    return a.sortOrder - b.sortOrder;
  });

  if (statusesLoading) {
    return <div className="p-6">Loading...</div>;
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader
        icon={Settings}
        title="General Settings"
        description="Manage campaign statuses, types, and application settings"
      >
        {statuses.length === 0 && (
          <Button
            variant="outline"
            onClick={() => createDefaultStatusesMutation.mutate()}
            disabled={createDefaultStatusesMutation.isPending}
          >
            Create Default Statuses
          </Button>
        )}
      </PageHeader>

      <Tabs defaultValue="statuses" className="space-y-4">
        <TabsList>
          <TabsTrigger value="statuses">Campaign Statuses</TabsTrigger>
          <TabsTrigger value="types">Campaign Types</TabsTrigger>
          <TabsTrigger value="workflow">Status Workflow</TabsTrigger>
          <TabsTrigger value="notifications">Mobile Notifications</TabsTrigger>
          <TabsTrigger value="ai-triage">AI Triage</TabsTrigger>
        </TabsList>

        <TabsContent value="statuses" className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">Campaign Statuses</h2>
            <Dialog open={isStatusDialogOpen} onOpenChange={setIsStatusDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => {
                  setEditingStatus(null);
                  resetStatusForm();
                }}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Status
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    {editingStatus ? "Edit Campaign Status" : "Create Campaign Status"}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="name">Status Name</Label>
                    <Input
                      id="name"
                      placeholder="e.g., Draft, Confirmed, Posted"
                      value={statusForm.name}
                      onChange={(e) => setStatusForm({ ...statusForm, name: e.target.value })}
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      placeholder="Describe what this status represents..."
                      value={statusForm.description}
                      onChange={(e) => setStatusForm({ ...statusForm, description: e.target.value })}
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="color">Color</Label>
                      <div className="flex gap-2">
                        <Input
                          type="color"
                          value={statusForm.color}
                          onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                          className="w-16 h-10 p-1"
                        />
                        <Input
                          placeholder="#6B7280"
                          value={statusForm.color}
                          onChange={(e) => setStatusForm({ ...statusForm, color: e.target.value })}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="sortOrder">Sort Order</Label>
                      <Input
                        id="sortOrder"
                        type="number"
                        value={statusForm.sortOrder}
                        onChange={(e) => setStatusForm({ ...statusForm, sortOrder: Number(e.target.value) })}
                      />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <Label htmlFor="isActive">Active Status</Label>
                    <Switch
                      id="isActive"
                      checked={statusForm.isActive}
                      onCheckedChange={(checked) => setStatusForm({ ...statusForm, isActive: checked })}
                    />
                  </div>

                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setIsStatusDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button
                      onClick={editingStatus ? handleUpdateStatus : handleCreateStatus}
                      disabled={createStatusMutation.isPending || updateStatusMutation.isPending}
                    >
                      {editingStatus ? "Update Status" : "Create Status"}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {orderedStatuses.map((status) => (
              <Card key={status.id}>
                <CardContent className="flex items-center justify-between p-4">
                  <div className="flex items-center gap-3">
                    <div
                      className="w-4 h-4 rounded-full"
                      style={{ backgroundColor: status.color }}
                    />
                    {getStatusIcon(status)}
                    <div>
                      <h3 className="font-semibold">{status.name}</h3>
                      <p className="text-sm text-muted-foreground">{status.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant={status.isActive ? "default" : "secondary"}>
                      {status.isActive ? "Active" : "Inactive"}
                    </Badge>
                    <span className="text-sm text-muted-foreground">#{status.sortOrder}</span>
                    <Button variant="ghost" size="sm" onClick={() => handleEditStatus(status)}>
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteStatusMutation.mutate(status.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="ai-triage" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Photo Checks standards</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Edit what Photo Checks looks for in store photos, then try changes safely before making them live.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setLocation("/admin/ai")}>
                  Open Photo Checks
                </Button>
                <Button variant="outline" onClick={() => setLocation("/admin/ai-triage/prompts")}>
                  Edit standards
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Photo Checks</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-sm text-muted-foreground">
                Home for standards, spotting store formats, and trying changes safely before go-live.
              </p>
              <div className="flex flex-wrap gap-2">
                <Button onClick={() => setLocation("/admin/ai")}>
                  Open Photo Checks
                </Button>
                <Button variant="outline" onClick={() => window.open("/admin/ai", "_blank")}>
                  Open in new tab
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="types" className="space-y-4">
          <CampaignTypes />
        </TabsContent>

        <TabsContent value="notifications" className="space-y-4">
          <MobileNotifications />
        </TabsContent>

        <TabsContent value="workflow" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Status Workflow Overview</CardTitle>
              <p className="text-sm text-muted-foreground">
                Statuses are listed in the order they are applied. Update sort order or activation in the Campaign
                Statuses tab to change this workflow.
              </p>
            </CardHeader>
            <CardContent className="space-y-6">
              {orderedStatuses.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No campaign statuses have been configured yet.
                </p>
              ) : (
                orderedStatuses.map((status, index) => (
                  <div key={status.id} className="flex items-start gap-4">
                    <div className="text-2xl font-bold text-muted-foreground">
                      {index + 1}
                    </div>
                    <div className="flex-1 space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold">{status.name}</span>
                        {!status.isActive && <Badge variant="secondary">Inactive</Badge>}
                      </div>
                      {status.description && (
                        <p className="text-sm text-muted-foreground">{status.description}</p>
                      )}
                      <div className="text-xs text-muted-foreground">
                        Sort Order: {status.sortOrder}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Color</span>
                      <div
                        className="w-6 h-6 rounded-full border"
                        style={{ backgroundColor: status.color }}
                      />
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
